import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { Button } from "../components/Button";
import { Lock, UserPlus, AlertCircle } from "lucide-react";
import styles from "./LoginPage.module.css";

interface InvitePageProps {
  token: string;
  onNavigateToLogin: (token: string) => void;
}

interface ValidateResponse {
  valid: boolean;
  email?: string | null;
  reason?: string | null;
}

export function InvitePage({ token, onNavigateToLogin }: InvitePageProps) {
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const [errorReason, setErrorReason] = useState<string | null>(null);

  useEffect(() => {
    async function validate() {
      try {
        const data = await api.get<ValidateResponse>(
          `/auth/invitations/${token}/validate`,
          { auth: false },
        );
        setValid(data.valid);
        setEmail(data.email ?? null);
        if (!data.valid) {
          setErrorReason(
            data.reason === "revoked"
              ? "Diese Einladung wurde widerrufen."
              : data.reason === "max_uses_reached"
                ? "Diese Einladung wurde bereits vollständig genutzt."
                : "Diese Einladung ist abgelaufen.",
          );
        }
      } catch {
        setValid(false);
        setErrorReason("Einladung nicht gefunden oder ungültig.");
      } finally {
        setLoading(false);
      }
    }

    validate();
  }, [token]);

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <div className={styles.logo}>
            <img src="/logo.png" alt="KollegePlus" className={styles.logoImg} />
          </div>
          <h1 className={styles.title}>KollegePlus</h1>
          <p className={styles.subtitle}>Du wurdest eingeladen!</p>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "2rem 0" }}>
            <p style={{ color: "var(--color-text-tertiary)" }}>Einladung wird geprüft...</p>
          </div>
        ) : valid ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                padding: "1rem",
                background: "rgba(34, 197, 94, 0.1)",
                borderRadius: "var(--radius-lg)",
                border: "1px solid rgba(34, 197, 94, 0.15)",
              }}
            >
              <UserPlus size={20} style={{ color: "#22C55E", flexShrink: 0 }} />
              <div>
                <p style={{ fontWeight: 600, color: "var(--color-text-primary)", fontSize: "var(--text-sm)" }}>
                  Einladung gültig
                </p>
                {email && (
                  <p style={{ fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", marginTop: "0.25rem" }}>
                    Für: {email}
                  </p>
                )}
              </div>
            </div>

            <Button
              size="lg"
              className={styles.submitButton}
              onClick={() => onNavigateToLogin(token)}
            >
              Jetzt registrieren
            </Button>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <div className={styles.error} style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
              <AlertCircle size={20} style={{ flexShrink: 0 }} />
              <span>{errorReason}</span>
            </div>

            <Button
              size="lg"
              variant="ghost"
              className={styles.submitButton}
              onClick={() => {
                window.history.pushState({}, "", "/");
                window.dispatchEvent(new PopStateEvent("popstate"));
              }}
            >
              Zur Anmeldung
            </Button>
          </div>
        )}

        <div className={styles.security}>
          <Lock size={14} /> Ende-zu-Ende verschlüsselt
        </div>
      </div>
    </div>
  );
}
